import useOrderPage from 'components/pages/OrderPage/useOrderPage';
import Logos from 'components/ui/Logo/Logos';
import Button from 'components/ui/Button/Button';
import PageSettings from 'components/ui/PageSettings/PageSettings';
import StepIndicator from 'components/ui/StepIndicator/StepIndicator';
import Form from 'components/ui/Form/Form';
import BaseControl from 'components/ui/Form/controls/BaseControl/BaseControl';
import Checkbox from 'components/ui/Form/controls/Checkbox/Checkbox';
import NumberInput from 'components/ui/Form/controls/NumberInput/NumberInput';
import TextInput from 'components/ui/Form/controls/TextInput/TextInput';
import { NumberFormatValues, SourceInfo } from 'react-number-format';
import { convertToHUF } from 'utils/helpers';
import { Product } from 'types/order';

const OrderPage = () => {
  const {
    orderData,
    formMethods,
    handleSubmit,
    isSubmitLoading,
    listProducts,
    isLoading,
    getByTax,
    ADDRESS
  } = useOrderPage();

  const handleChangeTax = async (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    const tax = (document.getElementsByName('tax')[0] as HTMLInputElement)?.value ?? '';
    const addr = (await getByTax(tax)) ?? { ...ADDRESS, tax };
    if (addr) {
      let elem = document.getElementsByName('company')[0] as HTMLInputElement;
      elem.focus();
      elem.value = addr?.name ?? '';
      elem = document.getElementsByName('zip')[0] as HTMLInputElement;
      elem.focus();
      elem.value = addr?.zip ?? '';
      elem = document.getElementsByName('city')[0] as HTMLInputElement;
      elem.focus();
      elem.value = addr?.city ?? '';
      elem = document.getElementsByName('street')[0] as HTMLInputElement;
      elem.focus();
      elem.value = addr?.street ?? '';
      elem = document.getElementsByName('streetType')[0] as HTMLInputElement;
      elem.focus();
      elem.value = addr?.streetType ?? '';
      elem = document.getElementsByName('house')[0] as HTMLInputElement;
      elem.focus();
      elem.value = addr?.house ?? '';
      elem = document.getElementsByName('staircase')[0] as HTMLInputElement;
      elem.focus();
      elem.value = addr?.staircase ?? '';
      elem = document.getElementsByName('floor')[0] as HTMLInputElement;
      elem.focus();
      elem.value = addr?.floor ?? '';
      elem = document.getElementsByName('door')[0] as HTMLInputElement;
      elem.focus();
      elem.value = addr?.door ?? '';
      elem = document.getElementsByName('tax')[0] as HTMLInputElement;
      elem.value = addr?.tax ?? '';
      elem.focus();
    }
  };

  if (isLoading) {
    return <div>loading</div>;
  }

  const onSubmit = () => {
    const order = {
      ...orderData,
      name: (document.getElementsByName('name')[0] as HTMLInputElement)?.value ?? '',
      email: (document.getElementsByName('email')[0] as HTMLInputElement)?.value ?? '',
      tel: (document.getElementsByName('tel')[0] as HTMLInputElement)?.value ?? '',
      cupon: (document.getElementsByName('cupon')[0] as HTMLInputElement)?.value ?? '',
      tax: (document.getElementsByName('tax')[0] as HTMLInputElement)?.value ?? '',
      company:
        (document.getElementsByName('company')[0] as HTMLInputElement)?.value ?? '',
      zip: (document.getElementsByName('zip')[0] as HTMLInputElement)?.value ?? '',
      city: (document.getElementsByName('city')[0] as HTMLInputElement)?.value ?? '',
      street: (document.getElementsByName('street')[0] as HTMLInputElement)?.value ?? '',
      streetType:
        (document.getElementsByName('streetType')[0] as HTMLInputElement)?.value ?? '',
      house: (document.getElementsByName('house')[0] as HTMLInputElement)?.value ?? '',
      staircase:
        (document.getElementsByName('staircase')[0] as HTMLInputElement)?.value ?? '',
      floor: (document.getElementsByName('floor')[0] as HTMLInputElement)?.value ?? '',
      door: (document.getElementsByName('door')[0] as HTMLInputElement)?.value ?? '',
      products: listProducts
        .filter(
          (product) =>
            (document.getElementsByName(`product_${product.id}`)[0] as HTMLInputElement)
              ?.checked
        )
        .map((product) => product.id)
    };
    handleSubmit(order);
  };

  return (
    <>
      <PageSettings />
      <div className="w-full p-8 text-darkBlue-100">
        <div className="flex justify-center mb-8">
          <div className="w-56">
            <img src="/megrendelo/assets/parolalogo.png" alt="Parola Kft." height={44} />
          </div>
        </div>
        <StepIndicator stepCount={3} currentStep={2} />
        <Form onSubmit={onSubmit} formMethods={formMethods}>
          <h2 className="text-3xl text-center my-8 font-semibold">Megrendelő lap</h2>
          <h3 className="text-xl font-semibold mb-4">Termék választás:</h3>
          <section className="pb-4 mb-4">
            <Form.Group>
              <BaseControl name="products">
                <>
                  {listProducts?.map((product: Product) => (
                    <Checkbox
                      name={`product_${product.id}`}
                      key={product.id}
                      label={
                        <div className="py-2">{`${product.name} (${
                          ((product.oldPrice ?? 0) > 0
                            ? `${convertToHUF(product.oldPrice ?? 0)} + ÁFA helyett `
                            : '') + convertToHUF(product.netPrice)
                        } + ÁFA)`}</div>
                      }
                    />
                  ))}
                </>
              </BaseControl>
            </Form.Group>
          </section>

          <h3 className="text-xl font-semibold mb-4">Felhasználói adatok:</h3>
          <section className="pb-4 mb-4">
            <TextInput name="name" label="Név:" />
            <TextInput name="email" type="email" label="E-mail cím:" />
            <TextInput name="tel" label="Telefonszám:" />
          </section>

          <h3 className="text-xl font-semibold mb-4">Kupon kód:</h3>
          <section className="pb-4 mb-4">
            <TextInput name="cupon" />
          </section>

          <h3 className="text-xl font-semibold mb-4">Számlázási adatok:</h3>
          <section className="pb-4 mb-4">
            <NumberInput
              name="tax"
              label="Adószám"
              format="########-#-##"
              mask="_"
              onValueChange={handleChangeTax}
            />
            <TextInput name="company" label="Számlázási név:" />
            <div className="flex flex-col lg:flex-row lg:justify-between">
              <TextInput
                name="zip"
                label="Irányítószám:"
                className="w-full lg:w-1/5 inline-block mr-5"
              />
              <TextInput name="city" label="Település:" className="w-full inline-block" />
            </div>
            <div className="flex flex-col lg:flex-row lg:justify-between">
              <TextInput
                name="street"
                label="Közterület neve:"
                className="w-full inline-block mr-5"
              />
              <TextInput
                name="streetType"
                label="Közterület jellege:"
                className="w-full lg:w-1/4 inline-block"
              />
            </div>
            <div className="flex flex-col lg:flex-row lg:justify-between">
              <TextInput
                name="house"
                label="Házszám:"
                className="w-full lg:w-1/4 inline-block mr-5"
              />
              <TextInput
                name="staircase"
                label="Épület, lépcsőház:"
                className="w-full lg:w-1/4 inline-block mr-5"
              />
              <TextInput
                name="floor"
                label="Emelet:"
                className="w-full lg:w-1/4 inline-block mr-5"
              />
              <TextInput
                name="door"
                label="Ajtó:"
                className="w-full lg:w-1/4 inline-block"
              />
            </div>
          </section>

          <section className="pb-4 mb-4">
            <div className="mt-8">
              <Logos url="https://simplepartner.hu/PaymentService/Fizetesi_tajekoztato.pdf" />
            </div>
            <div className="mt-8">
              <Form.Group>
                <Button
                  type="submit"
                  variant="default"
                  color="lightBlue"
                  fullWidth
                  loading={isSubmitLoading}
                >
                  Tovább
                </Button>
              </Form.Group>
            </div>
          </section>
        </Form>
      </div>
    </>
  );
};

export default OrderPage;
